import {
  normalizeRailedEdges,
  type StairsConfig,
  type Vertex,
} from "./geometry";
import type { RailColor, RailHeight } from "../three/buildDeck";

const POST_SPACING_FT = 4;
const CABLES_PER_RUN: Record<RailHeight, number> = { 36: 10, 42: 12 };

type ColorPricing = {
  postEach: number;
  topRailPerFoot: number;
  cablePerFoot: number;
  tensionerEach: number;
  mountKitEach: number;
};

const PRICING: Record<RailColor, ColorPricing> = {
  black: {
    postEach: 80,
    topRailPerFoot: 8,
    cablePerFoot: 3,
    tensionerEach: 25,
    mountKitEach: 12,
  },
  silver: {
    postEach: 120,
    topRailPerFoot: 12,
    cablePerFoot: 4,
    tensionerEach: 30,
    mountKitEach: 14,
  },
  white: {
    postEach: 90,
    topRailPerFoot: 9,
    cablePerFoot: 3,
    tensionerEach: 25,
    mountKitEach: 12,
  },
};

export type LineItem = {
  id: string;
  name: string;
  detail: string;
  qty: number;
  unit: string;
  unitPrice: number;
  subtotal: number;
};

export type PostBreakdown = {
  levelEnd: number;
  levelLine: number;
  levelCorner: number;
  angleEnd: number;
  angleLine: number;
};

export type Kit = {
  perimeterFt: number;
  totalRunFt: number;
  runs: number;
  posts: number;
  postBreakdown: PostBreakdown;
  cablesPerRun: number;
  cableFeet: number;
  tensionerSets: number;
  items: LineItem[];
  total: number;
};

type Input = {
  vertices: Vertex[];
  stairs: StairsConfig;
  railHeightInches: RailHeight;
  color: RailColor;
  railedEdges?: boolean[];
};

function round(n: number) {
  return Math.round(n * 100) / 100;
}

export function computeKit({
  vertices,
  stairs,
  railHeightInches,
  color,
  railedEdges,
}: Input): Kit {
  const n = vertices.length;
  const railed = normalizeRailedEdges(railedEdges, n);
  const stairEdgeRailed =
    stairs.edgeIndex < n ? railed[stairs.edgeIndex] : false;
  const stairRailed = stairs.enabled && stairs.withRailing && stairEdgeRailed;
  const stairFt = stairRailed ? stairs.lengthFt : 0;

  const levelLineFromEdge = (lenFt: number) =>
    Math.max(0, Math.ceil(lenFt / POST_SPACING_FT) - 1);

  // Aggregate per-edge contribution to railing footage, line posts, and run count.
  let perimeterRailedFt = 0;
  let levelLine = 0;
  let edgeRuns = 0; // distinct deck-side runs (a stair-railed edge splits in two)
  for (let i = 0; i < n; i++) {
    if (!railed[i]) continue;
    const a = vertices[i];
    const b = vertices[(i + 1) % n];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    perimeterRailedFt += len;
    if (stairRailed && i === stairs.edgeIndex) {
      const stairOpening = Math.min(len * 0.55, 7);
      const sideRun = Math.max(0, (len - stairOpening) / 2);
      levelLine += 2 * levelLineFromEdge(sideRun);
      edgeRuns += 2;
    } else {
      levelLine += levelLineFromEdge(len);
      edgeRuns += 1;
    }
  }

  // Corner posts: vertex with both adjacent edges railed.
  // End posts: vertex with exactly one adjacent edge railed (rail-to-deck transition).
  // No post: vertex with neither.
  let levelCorner = 0;
  let levelEndFromTransitions = 0;
  for (let i = 0; i < n; i++) {
    const incoming = railed[(i - 1 + n) % n];
    const outgoing = railed[i];
    if (incoming && outgoing) levelCorner++;
    else if (incoming || outgoing) levelEndFromTransitions++;
  }
  const levelEndFromStair = stairRailed ? 2 : 0;
  const levelEnd = levelEndFromTransitions + levelEndFromStair;

  const angleEnd = stairRailed ? 2 : 0;
  const angleLine = stairRailed ? 2 * levelLineFromEdge(stairs.lengthFt) : 0;

  const perimeterFt = perimeterRailedFt;
  const totalRunFt = perimeterRailedFt + stairFt;
  const runs = edgeRuns + (stairRailed ? 1 : 0);

  const postBreakdown: PostBreakdown = {
    levelEnd,
    levelLine,
    levelCorner,
    angleEnd,
    angleLine,
  };
  const posts =
    levelEnd + levelLine + levelCorner + angleEnd + angleLine;
  const cablesPerRun = CABLES_PER_RUN[railHeightInches];
  const cableFeet = totalRunFt * cablesPerRun;
  const tensionerSets = runs * cablesPerRun;
  const p = PRICING[color];

  const detail = `${runs} runs · ${n}-sided deck${stairs.enabled ? " + stairs" : ""}`;

  const items: LineItem[] = [
    {
      id: "post",
      name: "Posts",
      detail: `${railHeightInches}″ powder-coated steel`,
      qty: posts,
      unit: "ea",
      unitPrice: p.postEach,
      subtotal: round(posts * p.postEach),
    },
    {
      id: "top-rail",
      name: "Top rail",
      detail,
      qty: round(totalRunFt),
      unit: "ft",
      unitPrice: p.topRailPerFoot,
      subtotal: round(totalRunFt * p.topRailPerFoot),
    },
    {
      id: "cable",
      name: "Cable",
      detail: `${cablesPerRun} cables × ${runs} runs`,
      qty: round(cableFeet),
      unit: "ft",
      unitPrice: p.cablePerFoot,
      subtotal: round(cableFeet * p.cablePerFoot),
    },
    {
      id: "tensioner",
      name: "Tensioner sets",
      detail: "End fittings + tensioning hardware",
      qty: tensionerSets,
      unit: "ea",
      unitPrice: p.tensionerEach,
      subtotal: round(tensionerSets * p.tensionerEach),
    },
    {
      id: "mount",
      name: "Post mounting hardware",
      detail: "Surface-mount base plates",
      qty: posts,
      unit: "kit",
      unitPrice: p.mountKitEach,
      subtotal: round(posts * p.mountKitEach),
    },
  ];

  const total = round(items.reduce((sum, i) => sum + i.subtotal, 0));

  return {
    perimeterFt,
    totalRunFt,
    runs,
    posts,
    postBreakdown,
    cablesPerRun,
    cableFeet,
    tensionerSets,
    items,
    total,
  };
}
